import React, { useState } from 'react';
import { Button, Badge, Modal, Tab, Tabs, Card, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { BsCalendar, BsClock, BsPeople, BsMic, BsChat, BsThreeDotsVertical, BsXLg, BsSearch, BsPlus } from 'react-icons/bs';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Header from "../Header";
import Sidebar from "../SideBar";
import Content from '../Content';
import MeetingForm from './MeetingForm';

// Setup calendar localization
const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const Meetings = () => {
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Sample meeting data
  const meetings = [
   
    {
      id: 4,
      title: "Client Presentation",
      start: new Date(2023, 5, 17, 13, 0),
      end: new Date(2023, 5, 17, 14, 0),
      organizer: "Emily Rodriguez",
      participants: 15,
      status: "upcoming",
      type: "video",
      agenda: "Showcase new features to client",
      description: "Demonstrate latest product features to key client"
    }
  ];

  // Filter meetings based on search query
  const filteredMeetings = meetings.filter(meeting => 
    meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    meeting.organizer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    meeting.agenda.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calendar event handling
  const handleSelectEvent = (event) => {
    setSelectedMeeting(event);
  };

  const handleSelectSlot = (slotInfo) => {
    setShowForm(true);
  };

  const getStatusVariant = (status) => {
    switch(status) {
      case 'upcoming': return 'primary';
      case 'completed': return 'secondary';
      case 'canceled': return 'danger';
      default: return 'light';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'video': return <BsMic className="me-1" />;
      case 'audio': return <BsMic className="me-1" />;
      case 'hybrid': return <><BsMic className="me-1" /><BsPeople /></>;
      default: return <BsPeople />;
    }
  };

  return (
    <div className="app">
      <Sidebar />
      <div className="main-content">
        <Header />
        <Content>
          <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0 fw-bold">Meetings</h2>

              <MeetingForm show={showForm} handleClose={() => setShowForm(false)} />
            </div>

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4 border-0"
            >
              <Tab eventKey="list" title="List View" className="border-0">
                <div className="row">
                  <div className="col-lg-8">
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="btn-group btn-group-sm me-3">
                            <Button variant="outline-primary" active>All</Button>
                            <Button variant="outline-primary">Upcoming</Button>
                            <Button variant="outline-primary">Completed</Button>
                          </div>
                          <InputGroup className="ms-auto" style={{maxWidth: '300px'}}>
                            <InputGroup.Text className="bg-white border-end-0">
                              <BsSearch />
                            </InputGroup.Text>
                            <Form.Control
                              placeholder="Search meetings..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="border-start-0"
                            />
                          </InputGroup>
                        </div>
                      </Card.Body>
                    </Card>

                    <div className="meeting-list">
                      {filteredMeetings.map(meeting => (
                        <Card key={meeting.id} className="mb-3 border-0 shadow-sm">
                          <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div className="d-flex align-items-center">
                                <Badge bg={getStatusVariant(meeting.status)} className="me-3">
                                  {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                                </Badge>
                                <span className="text-muted small d-flex align-items-center">
                                  {getTypeIcon(meeting.type)} 
                                  <span className="ms-1">{meeting.type.charAt(0).toUpperCase() + meeting.type.slice(1)}</span>
                                </span>
                              </div>
                              <Dropdown>
                                <Dropdown.Toggle variant="link" className="text-muted p-0">
                                  <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item>Edit</Dropdown.Item>
                                  <Dropdown.Item>Duplicate</Dropdown.Item>
                                  <Dropdown.Item>Cancel</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <h5 className="mb-2 fw-bold">{meeting.title}</h5>
                            <p className="text-muted mb-3">{meeting.agenda}</p>

                            <div className="d-flex flex-wrap align-items-center text-muted mb-3">
                              <div className="me-3 mb-2 d-flex align-items-center">
                                <BsCalendar className="me-2" />
                                <span className="small">{format(meeting.start, 'MMM dd, yyyy')}</span>
                              </div>
                              <div className="me-3 mb-2 d-flex align-items-center">
                                <BsClock className="me-2" />
                                <span className="small">{format(meeting.start, 'h:mm a')} - {format(meeting.end, 'h:mm a')}</span>
                              </div>
                              <div className="me-3 mb-2 d-flex align-items-center">
                                <BsPeople className="me-2" />
                                <span className="small">{meeting.participants} participants</span>
                              </div>
                              <div className="me-3 mb-2">
                                <span className="small">Organizer: <strong>{meeting.organizer}</strong></span>
                              </div>
                            </div>

                            <div className="d-flex justify-content-end">
                              {meeting.status === 'upcoming' && (
                                <>
                                  <Button variant="outline-secondary" size="sm" className="me-2 d-flex align-items-center">
                                    <BsChat className="me-1" />
                                    Message
                                  </Button>
                                  <Button variant="primary" size="sm" className="px-3">
                                    Join Meeting
                                  </Button>
                                </>
                              )}
                              {meeting.status === 'completed' && (
                                <Button variant="outline-primary" size="sm">
                                  View Recording
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <Card className="border-0 shadow-sm sticky-top" style={{top: '20px'}}>
                      <Card.Body>
                        <h5 className="mb-3 fw-bold">Today's Schedule</h5>
                        <div style={{ height: '300px' }}>
                          <Calendar
                            localizer={localizer}
                            events={meetings.filter(m => 
                              m.start.toDateString() === new Date().toDateString()
                            )}
                            startAccessor="start"
                            endAccessor="end"
                            defaultView={Views.DAY}
                            views={[Views.DAY]}
                            toolbar={false}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="calendar" title="Calendar View">
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <Calendar
                      localizer={localizer}
                      events={meetings}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '70vh' }}
                      onSelectEvent={handleSelectEvent}
                      onSelectSlot={handleSelectSlot}
                      selectable
                      defaultView={Views.WEEK}
                      views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                      eventPropGetter={(event) => ({
                        style: {
                          backgroundColor: 
                            event.status === 'completed' ? '#6c757d' :
                            event.status === 'canceled' ? '#dc3545' : '#0d6efd',
                          border: 'none',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                        }
                      })}
                    />
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </div>

          {/* Meeting Form Modal */}
          <MeetingForm show={showForm} handleClose={() => setShowForm(false)} />

          {/* Meeting Detail Modal */}
          <Modal show={!!selectedMeeting} onHide={() => setSelectedMeeting(null)} size="lg" centered>
            <Modal.Header className="border-0 pb-0">
              <Modal.Title className="fw-bold">{selectedMeeting?.title}</Modal.Title>
              <Button variant="link" onClick={() => setSelectedMeeting(null)}>
                <BsXLg />
              </Button>
            </Modal.Header>
            <Modal.Body className="pt-0">
              {selectedMeeting && (
                <div>
                  <div className="d-flex flex-wrap mb-4">
                    <div className="me-4 mb-3">
                      <h6 className="text-muted small mb-1">Date</h6>
                      <p className="mb-0 d-flex align-items-center">
                        <BsCalendar className="me-2" />
                        {format(selectedMeeting.start, 'MMMM d, yyyy')}
                      </p>
                    </div>
                    <div className="me-4 mb-3">
                      <h6 className="text-muted small mb-1">Time</h6>
                      <p className="mb-0 d-flex align-items-center">
                        <BsClock className="me-2" />
                        {format(selectedMeeting.start, 'h:mm a')} - {format(selectedMeeting.end, 'h:mm a')}
                      </p>
                    </div>
                    <div className="me-4 mb-3">
                      <h6 className="text-muted small mb-1">Type</h6>
                      <p className="mb-0 d-flex align-items-center">
                        {getTypeIcon(selectedMeeting.type)} 
                        <span className="ms-2">{selectedMeeting.type}</span>
                      </p>
                    </div>
                    <div className="me-4 mb-3">
                      <h6 className="text-muted small mb-1">Organizer</h6>
                      <p className="mb-0 fw-bold">{selectedMeeting.organizer}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="fw-bold mb-3">Agenda</h5>
                    <p className="text-muted">{selectedMeeting.agenda}</p>
                  </div>

                  <div className="mb-4">
                    <h5 className="fw-bold mb-3">Description</h5>
                    <p className="text-muted">{selectedMeeting.description}</p>
                  </div>

                  <div>
                    <h5 className="fw-bold mb-3">Participants ({selectedMeeting.participants})</h5>
                    <div className="participant-list">
                      {Array.from({ length: selectedMeeting.participants }).map((_, i) => (
                        <Badge key={i} bg="light" text="dark" className="me-2 mb-2 p-2">
                          participant{i + 1}@example.com
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="outline-secondary" onClick={() => setSelectedMeeting(null)}>
                Close
              </Button>
              <Button variant="primary" className="px-4">
                {selectedMeeting?.status === 'upcoming' ? 'Join Meeting' : 'View Recording'}
              </Button>
            </Modal.Footer>
          </Modal>
        </Content>
      </div>
    </div>
  );
};

export default Meetings;